import React, { useEffect, useRef } from "react";
import { pickColor, hexToRgb, rgba } from "./internal/colorUtils";

export const key = "equalizer-bars";

const DEFAULT_BG = "#0b1020";
const DEFAULT_LOW = "#22d3ee";
const DEFAULT_HIGH = "#f472b6";
const DEFAULT_PEAK = "#fde68a";

// A spectrum-analyzer fake. We don't have audio, so we synthesize a
// plausible-looking spectrum:
//   - a handful of "tones" wander through the frequency band
//   - each tone deposits a Gaussian bump on the spectrum (centred on its
//     freq, width = bandwidth, height = amplitude)
//   - the whole spectrum is then multiplied by a beat envelope that
//     spikes periodically and decays exponentially (the "kick drum")
//   - per-bar smoothing damps the value toward the target so bars don't
//     jitter at every frame
//   - per-bar "peaks" hold the recent max and fall slowly, classic VU
//     style.
const BAR_COUNT = 64;
const BAR_GAP_FRAC = 0.18;        // gap as a fraction of a bar's slot width
const BOTTOM_PAD_FRAC = 0.04;     // padding under the bars
const TOP_PAD_FRAC = 0.06;        // padding above the tallest possible bar
// Smoothing tuned for a calmer, more contemplative motion. Bars still
// rise faster than they fall (classic VU asymmetry), just gentler.
const SMOOTHING_UP = 0.28;        // how aggressively a bar follows a rising target
const SMOOTHING_DOWN = 0.05;      // how aggressively a bar follows a falling target (slower → springier)
const PEAK_DROP_PER_FRAME = 0.0028;// how fast the peak marker descends
const BEAT_INTERVAL_MS = 1300;    // base time between kicks (slower, more spaced out)
const BEAT_DECAY_MS = 700;        // time constant of the kick decay (longer "tail")
const BEAT_GAIN = 0.45;           // peak boost added by each kick

// Each "tone" is a moving bump in the spectrum. Wider bandwidth = broader
// hump (e.g. low pad), narrow = a sharp spike (e.g. lead). centerFreq and
// amp evolve over time so the spectrum never sits still. Speeds halved
// vs. the original tuning so tones drift across the band gently.
const TONES = [
  { speed: 0.00010, ampSpeed: 0.00050, amp: 0.55, bandwidth: 0.20 },
  { speed: 0.00018, ampSpeed: 0.00080, amp: 0.40, bandwidth: 0.10 },
  { speed: 0.00027, ampSpeed: 0.00110, amp: 0.45, bandwidth: 0.07 },
  { speed: 0.00013, ampSpeed: 0.00060, amp: 0.50, bandwidth: 0.13 },
  { speed: 0.00006, ampSpeed: 0.00040, amp: 0.50, bandwidth: 0.30 },
];

function lerp(a, b, t) { return a + (b - a) * t; }

export default function EqualizerBars({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef([DEFAULT_BG, DEFAULT_LOW, DEFAULT_HIGH, DEFAULT_PEAK]);
  const valuesRef = useRef(new Float32Array(BAR_COUNT));
  const peaksRef = useRef(new Float32Array(BAR_COUNT));
  const startRef = useRef(0);
  const lastBeatRef = useRef(0);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_LOW),
      pickColor(colors, 2, DEFAULT_HIGH),
      pickColor(colors, 3, DEFAULT_PEAK),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const rect = parent.getBoundingClientRect();
      width = Math.max(1, Math.floor(rect.width));
      height = Math.max(1, Math.floor(rect.height));
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const tick = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const t = ts - startRef.current;
      const [bg, lowCol, highCol, peakCol] = colorsRef.current;
      const lowRgb = hexToRgb(lowCol) || hexToRgb(DEFAULT_LOW);
      const highRgb = hexToRgb(highCol) || hexToRgb(DEFAULT_HIGH);

      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);

      // Beat envelope: every BEAT_INTERVAL_MS we register a "hit" and
      // decay it exponentially. Result is a 0..BEAT_GAIN multiplicative
      // boost on top of the spectrum.
      if (!lastBeatRef.current) lastBeatRef.current = t;
      while (t - lastBeatRef.current > BEAT_INTERVAL_MS) {
        lastBeatRef.current += BEAT_INTERVAL_MS;
      }
      const sinceBeat = t - lastBeatRef.current;
      const beatBoost = BEAT_GAIN * Math.exp(-sinceBeat / BEAT_DECAY_MS);

      // Compute a target spectrum value for each bar from the moving
      // tones, then smooth toward it.
      const values = valuesRef.current;
      const peaks = peaksRef.current;
      for (let i = 0; i < BAR_COUNT; i += 1) {
        // Map bar index to a frequency in [0..1]. Use a slight log curve
        // so low frequencies get more visual real estate (matches how
        // real EQs look).
        const freq = (i / (BAR_COUNT - 1)) ** 1.05;

        let spectrum = 0;
        for (let k = 0; k < TONES.length; k += 1) {
          const tone = TONES[k];
          // Tone center wanders in [0..1] via a slow sine. ampSpeed
          // independently modulates the contribution amplitude so tones
          // breathe in and out.
          const center = 0.5 + 0.5 * Math.sin(t * tone.speed + k * 1.7);
          const amp = tone.amp * (0.55 + 0.45 * Math.sin(t * tone.ampSpeed + k * 0.9));
          const dx = (freq - center) / Math.max(0.01, tone.bandwidth);
          spectrum += amp * Math.exp(-dx * dx);
        }
        // Add a small noise floor so bars never hit exactly 0 (looks
        // dead) and apply the beat envelope.
        spectrum = Math.min(1.0, spectrum * (1 + beatBoost) + 0.05);

        // Asymmetric smoothing: rise quickly, fall slowly → the classic
        // springy VU look.
        const prev = values[i];
        const target = spectrum;
        const k = target > prev ? SMOOTHING_UP : SMOOTHING_DOWN;
        values[i] = prev + (target - prev) * k;

        // Peak follows the bar up instantly, then drifts down at a fixed
        // rate.
        if (values[i] > peaks[i]) peaks[i] = values[i];
        else peaks[i] = Math.max(values[i], peaks[i] - PEAK_DROP_PER_FRAME);
      }

      // Render bars.
      const usableH = height * (1 - BOTTOM_PAD_FRAC - TOP_PAD_FRAC);
      const baseY = height * (1 - BOTTOM_PAD_FRAC);
      const slotW = width / BAR_COUNT;
      const barW = slotW * (1 - BAR_GAP_FRAC);

      for (let i = 0; i < BAR_COUNT; i += 1) {
        const x = i * slotW + (slotW - barW) / 2;
        const v = values[i];
        const h = v * usableH;
        const y = baseY - h;

        // Color gradient along the bar's height: low freq color at the
        // base, high freq at the top — matches the "cool to hot" mental
        // model of an EQ.
        const grad = ctx.createLinearGradient(0, baseY, 0, y);
        grad.addColorStop(0, `rgba(${lowRgb.r}, ${lowRgb.g}, ${lowRgb.b}, 0.95)`);
        grad.addColorStop(1, `rgba(${highRgb.r}, ${highRgb.g}, ${highRgb.b}, 0.95)`);
        ctx.fillStyle = grad;
        ctx.fillRect(x, y, barW, h);

        // Subtle "glow" cap — a faint warm halo at the bar tip sells the
        // luminous quality.
        const glowGrad = ctx.createLinearGradient(0, y - 8, 0, y + 8);
        const tipR = lerp(lowRgb.r, highRgb.r, v);
        const tipG = lerp(lowRgb.g, highRgb.g, v);
        const tipB = lerp(lowRgb.b, highRgb.b, v);
        glowGrad.addColorStop(0, `rgba(${tipR}, ${tipG}, ${tipB}, 0)`);
        glowGrad.addColorStop(0.5, `rgba(${tipR}, ${tipG}, ${tipB}, 0.55)`);
        glowGrad.addColorStop(1, `rgba(${tipR}, ${tipG}, ${tipB}, 0)`);
        ctx.fillStyle = glowGrad;
        ctx.fillRect(x - 1, y - 6, barW + 2, 12);

        // Peak marker — short bright bar a few px above the current
        // bar, painted in the dedicated peak color.
        const peakH = peaks[i] * usableH;
        const peakY = baseY - peakH;
        ctx.fillStyle = rgba(peakCol, 0.9);
        ctx.fillRect(x, Math.max(0, peakY - 2), barW, 2);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
